import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Slider } from "@/components/ui/slider";
import { CheckCircle2, ArrowRight, Calculator } from "lucide-react";

const AD_SIZES = [
  { label: "Hero — 4×9 in — $1,800/drop", price: 1800 },
  { label: "Horizon — 4×6 in — $1,300/drop", price: 1300 },
  { label: "Spotlight — 4×3 in — $900/drop", price: 900 },
];

const HOMES_PER_DROP = 10000;

const PROFESSIONS = [
  { label: "Custom / Other", value: null },
  { label: "HVAC", value: 3500 },
  { label: "Roofing", value: 8500 },
  { label: "Plumbing", value: 1200 },
  { label: "Water/Fire Restoration", value: 5000 },
  { label: "Real Estate Agent", value: 9000 },
  { label: "Electrician", value: 1500 },
  { label: "Landscaping / Lawn Care", value: 2500 },
  { label: "Painting", value: 3000 },
  { label: "Dentist", value: 1200 },
  { label: "Accountant / CPA", value: 1500 },
  { label: "Chiropractor", value: 1500 },
  { label: "Med Spa / Aesthetics", value: 2000 },
  { label: "Auto Repair", value: 900 },
];

const usd0 = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
const usd2 = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
const num = (n) => new Intl.NumberFormat("en-US").format(n);

const FACTS = [
  "Direct mail achieves a 112% ROI for professional services — the highest of any offline channel (ANA/DMA 2023).",
  "73% of U.S. consumers prefer direct mail for brand discovery. 79% act on it immediately vs. 45% for email.",
  "Oversized postcards (like the SAM 9×12) see response rates 2–3× higher than standard letter-size mail.",
  "Repeat mailings to the same route boost response 25–50% per subsequent drop due to brand familiarity.",
  "The average cost-per-lead for home-service direct mail is $30–$50 — far below Google Ads ($75–$150+).",
];

function Field({ label, value, children, help, ticks }) {
  return (
    <div className="mb-8">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-sam-crimson">{label}</span>
        <span className="font-display text-2xl font-extrabold text-sam-navy tabular-nums">{value}</span>
      </div>
      <div className="mt-3">{children}</div>
      {ticks && (
        <div className="mt-1.5 flex justify-between text-[10px] text-sam-slate/70">
          {ticks.map((t, i) => <span key={t} className={i === 1 ? "text-sam-slate font-semibold" : ""}>{t}</span>)}
        </div>
      )}
      {help && <p className="mt-2 text-xs leading-relaxed text-sam-slate">{help}</p>}
    </div>
  );
}

function ResultRow({ label, value, accent, last }) {
  const cls = accent === "profit" ? "text-sam-navy text-2xl" : accent === "roi" ? "text-sam-crimson text-3xl"
    : accent === "invest" ? "text-sam-crimson/80 text-xl" : "text-sam-navy text-2xl";
  return (
    <div className={`flex items-center justify-between py-3.5 ${last ? "" : "border-b border-sam-navy/10"}`}>
      <span className="text-sam-slate">{label}</span>
      <span className={`font-display font-extrabold tabular-nums ${cls}`}>{value}</span>
    </div>
  );
}

export function ROICalculatorWidget({ heading = true }) {
  const [sizeIdx, setSizeIdx] = useState(1);
  const [drops, setDrops] = useState(3);
  const [response, setResponse] = useState(0.5);
  const [close, setClose] = useState(20);
  const [jobValue, setJobValue] = useState(2500);
  const [profIdx, setProfIdx] = useState(0);

  const r = useMemo(() => {
    const price = AD_SIZES[sizeIdx].price;
    const homes = drops * HOMES_PER_DROP;
    const leads = Math.round(homes * (response / 100));
    const deals = Math.round(leads * (close / 100));
    const revenue = deals * jobValue;
    const investment = drops * price;
    const net = revenue - investment;
    const roi = investment > 0 ? (net / investment) * 100 : 0;
    const costLead = leads > 0 ? investment / leads : 0;
    const costClose = deals > 0 ? investment / deals : 0;
    return { homes, leads, deals, revenue, investment, net, roi, costLead, costClose };
  }, [sizeIdx, drops, response, close, jobValue]);

  return (
    <div>
      {heading && (
        <>
          <div className="flex items-center gap-2 text-sam-crimson mb-2"><Calculator className="h-5 w-5" /><span className="text-xs font-bold uppercase tracking-[0.2em]">Postcard ROI Calculator</span></div>
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold">See what a <span className="text-sam-crimson">Saving America Money</span> mailer can return.</h2>
          <p className="text-sam-slate mt-2 max-w-2xl">Adjust your assumptions and watch the numbers update live. Benchmarks reflect U.S. direct-mail averages for local & home-service businesses.</p>
        </>
      )}

      <div className="mt-10 grid lg:grid-cols-2 gap-6">
        {/* Inputs */}
        <div className="rounded-2xl border border-sam-navy/15 bg-white p-6 sm:p-8 shadow-sm" data-testid="roi-inputs">
          <div className="mb-8">
            <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-sam-crimson">Ad Size</span>
            <select
              data-testid="roi-adsize"
              value={sizeIdx}
              onChange={(e) => setSizeIdx(Number(e.target.value))}
              className="mt-3 w-full rounded-lg border border-sam-navy/20 bg-sam-cream px-4 py-3 text-sam-navy text-[15px] font-semibold focus:outline-none focus:ring-2 focus:ring-sam-navy/40 appearance-none bg-[url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2012%2012%22%3E%3Cpath%20fill%3D%22%230A192F%22%20d%3D%22M6%208L1%203h10z%22%2F%3E%3C%2Fsvg%3E')] bg-[right_1rem_center] bg-no-repeat"
            >
              {AD_SIZES.map((s, i) => <option key={s.label} value={i}>{s.label}</option>)}
            </select>
          </div>

          <Field label="Number of Drops" value={drops} ticks={["1 drop", "6 drops", "12 drops"]} help="USA avg: businesses see 3× higher recall after 3+ mailings to the same area.">
            <Slider className="roi-slider" value={[drops]} min={1} max={12} step={1} onValueChange={([v]) => setDrops(v)} data-testid="roi-drops" />
          </Field>

          <Field label="Response Rate" value={`${response.toFixed(1)}%`} ticks={["0.1%", "2.5% (avg)", "5%"]} help="USA benchmark: Direct mail averages 2.7–4.4% response for professional services (DMA). EDDM postcards average 0.5–1.5% on cold drops.">
            <Slider className="roi-slider" value={[response]} min={0.1} max={5} step={0.1} onValueChange={([v]) => setResponse(v)} data-testid="roi-response" />
          </Field>

          <Field label="Close Rate" value={`${close}%`} ticks={["5%", "25% (avg)", "60%"]} help="USA benchmark: Home-service pros close 20–30% of qualified leads. Top performers hit 40–50%.">
            <Slider className="roi-slider" value={[close]} min={5} max={60} step={1} onValueChange={([v]) => setClose(v)} data-testid="roi-close" />
          </Field>

          <div className="mb-8">
            <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-sam-crimson">Your Profession</span>
            <select
              data-testid="roi-profession"
              value={profIdx}
              onChange={(e) => { const i = Number(e.target.value); setProfIdx(i); const v = PROFESSIONS[i].value; if (v != null) setJobValue(v); }}
              className="mt-3 w-full rounded-lg border border-sam-navy/20 bg-sam-cream px-4 py-3 text-sam-navy text-[15px] font-semibold focus:outline-none focus:ring-2 focus:ring-sam-navy/40 appearance-none bg-[url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2012%2012%22%3E%3Cpath%20fill%3D%22%230A192F%22%20d%3D%22M6%208L1%203h10z%22%2F%3E%3C%2Fsvg%3E')] bg-[right_1rem_center] bg-no-repeat"
            >
              {PROFESSIONS.map((p, i) => <option key={p.label} value={i}>{p.label}{p.value != null ? ` — ${usd0(p.value)} avg job` : ""}</option>)}
            </select>
          </div>

          <Field label="Average Job Value" value={usd0(jobValue)} ticks={["$250", "$7,500", "$15,000"]} help="USA avg job value: HVAC $3,500 · Roofing $8,500 · Plumbing $1,200 · Restoration $5,000 · Real Estate Agent $9,000 commission · Electrician $1,500 · Landscaping $2,500 · Painting $3,000 · Dentist $1,200 · Accountant/CPA $1,500 · Chiropractor $1,500 · Med Spa $2,000 · Auto Repair $900. Pick your profession above or drag to match your own.">
            <Slider className="roi-slider" value={[jobValue]} min={250} max={15000} step={50} onValueChange={([v]) => { setJobValue(v); setProfIdx(0); }} data-testid="roi-jobvalue" />
          </Field>
        </div>

        {/* Results */}
        <div className="space-y-6">
          <div className="rounded-2xl border border-sam-navy/15 bg-white shadow-sm overflow-hidden" data-testid="roi-results">
            <div className="sam-flag-bar h-1 w-full" />
            <div className="p-6 sm:p-8">
              <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-sam-crimson mb-1">Campaign Results ({drops} drops × {num(HOMES_PER_DROP)} homes)</div>
              <div className="mt-3">
                <ResultRow label="Homes Reached" value={num(r.homes)} />
                <ResultRow label="Leads Generated" value={num(r.leads)} />
                <ResultRow label="Deals Closed" value={num(r.deals)} />
                <ResultRow label="Revenue" value={usd0(r.revenue)} />
                <ResultRow label="Total Investment" value={`-${usd0(r.investment)}`} accent="invest" />
                <ResultRow label="Net Profit" value={`+${usd0(r.net)}`} accent="profit" />
                <ResultRow label="Return on Investment" value={`${r.roi >= 0 ? "+" : ""}${Math.round(r.roi)}%`} accent="roi" last />
              </div>
              <div className="mt-5 grid grid-cols-2 gap-3">
                <div className="rounded-xl border border-sam-navy/15 bg-sam-navy/[0.03] p-4 text-center">
                  <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-sam-slate">Cost / Lead</div>
                  <div className="font-display text-xl font-extrabold text-sam-navy mt-1" data-testid="roi-cost-lead">{usd2(r.costLead)}</div>
                </div>
                <div className="rounded-xl border border-sam-navy/15 bg-sam-navy/[0.03] p-4 text-center">
                  <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-sam-slate">Cost / Close</div>
                  <div className="font-display text-xl font-extrabold text-sam-navy mt-1" data-testid="roi-cost-close">{usd2(r.costClose)}</div>
                </div>
              </div>
              <Link to="/postcards" className="mt-5 flex items-center justify-center gap-2 rounded-xl bg-sam-crimson hover:bg-sam-crimson/90 text-white font-bold py-3 transition-colors" data-testid="roi-cta">
                Reserve my postcard slot <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>

          <div className="rounded-2xl border border-sam-navy/15 bg-white p-6 sm:p-8 shadow-sm">
            <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-sam-crimson mb-4">U.S. Direct Mail Industry Facts</div>
            <ul className="space-y-3.5">
              {FACTS.map((f) => (
                <li key={f} className="flex gap-3 text-sm text-sam-slate leading-relaxed">
                  <CheckCircle2 className="h-4 w-4 text-sam-navy shrink-0 mt-0.5" />{f}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ROICalculator() {
  return (
    <div className="min-h-screen bg-sam-cream text-sam-navy">
      <div className="sam-flag-bar h-1.5 w-full" />
      <div className="mx-auto max-w-6xl px-4 sm:px-6 py-12">
        <ROICalculatorWidget heading />
      </div>
    </div>
  );
}
